"use client";


import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Button,
  Divider,
  ListSubheader,
  IconButton,
  Drawer,
  Stack,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import dynamic from "next/dynamic";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Header from "@/components/header";
import "./layout.css";
import useDevice from "@/hooks/useDevice";
import { useConversational } from "@/context/ConversationalContext";
import { formatRelativeTime } from "@/utils";
import gaService from "@/api/services/ga.service";
import { useUser } from "@/context/userContext";
import {
  ALL_CATEGORY,
  ALL_CITY,
  useFiltersStore,
} from "@/store/useFilterStore";
import {
  getStoredUserEmail,
  isLiveOrderEmail,
} from "@/utils/accessControl";

const LoginModal = dynamic(() => import("@/components/loginModal"), {
  ssr: false,
});

interface SidebarProps {
  children: ReactNode;
  chats?: {
    userId: string;
    firstMessage: string;
    sessionId: string;
    createdAt: string;
  }[];
  onNewChat?: () => void;
  onOpenChat?: (index: number, title: string) => void;
  onOpenDashboard?: (slug: string) => void;
}

const DASHBOARDS = [
  {
    title: "Dashboard",
    slug: "",
    icon: "https://sr-cdn-1.shiprocket.in/sr-promise/images/order_trends.svg",
  },
  {
    title: "Order Trends",
    slug: "order-trends",
    icon: "https://sr-cdn-1.shiprocket.in/sr-promise/images/order_trends.svg",
  },
  {
    title: "Buyer Stats",
    slug: "buyer-stats",
    icon: "https://sr-cdn-1.shiprocket.in/sr-promise/images/buyer_stats.svg",
  },
  {
    title: "RTO Trends",
    slug: "rto-trends",
    icon: "https://sr-cdn-1.shiprocket.in/sr-promise/images/rto_trends.svg",
  },
  {
    title: "Category Trends",
    slug: "category-trends",
    icon:
      "https://sr-cdn-1.shiprocket.in/sr-promise/images/category_trends.svg",
  },
  {
    title: "Geographical Trends",
    slug: "geo-trends",
    icon:
      "https://sr-cdn-1.shiprocket.in/sr-promise/images/geographical_trends.svg",
  },
  {
    title: "User Activity Stream",
    slug: "uc-events",
    icon:
      "https://sr-cdn-1.shiprocket.in/sr-promise/images/category_trends.svg",
  },
  {
    title: "Live Orders",
    slug: "live-order",
    icon:
      "https://sr-cdn-1.shiprocket.in/sr-promise/images/geographical_trends.svg",
  },
];

const drawerWidth = 280;
const NON_FILTER_DASHBOARDS = new Set(["live-order", "uc-events"]);

function slugifyLabel(label: string) {
  return String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "");
}

function toTimeSlug(key?: string | null) {
  switch (key) {
    case "LAST_MONTH":
      return "lastmonth";
    case "LAST_QUARTER":
      return "last3months";
    case "LAST_HALF_YEAR":
      return "last6months";
    default:
      return null;
  }
}

function SidebarContent({
  pathname,
  onNewChat,
  handleDrawerToggle,
}: {
  pathname: string;
  onNewChat?: () => void;
  handleDrawerToggle: Dispatch<SetStateAction<boolean>>;
}) {
  const [selectedChatIndex, setSelectedChatIndex] = useState<number | null>(
    null
  );

  const router = useRouter();
  const searchParams = useSearchParams();
  const isMobile = useDevice();
  const { user } = useUser();
  const {
    selectedState,
    selectedCity,
    selectedCategory,
    selectedDateRange,
  } = useFiltersStore();

  const {
    sessions,
    sessionsStatus,
    loadSessions,
    clearMessages,
    setCurrentSessionId,
    setIsLoading,
    setIsStreaming,
    setStreamingMsg,
    isNewChat,
  } = useConversational();

  const urlSessionId = searchParams?.get("sid") ?? null;
  const dashboardQueryString = (() => {
    const raw = searchParams?.toString() ?? "";
    if (!raw) return "";
    const query = new URLSearchParams(raw);
    const filterKeys = [
      "state",
      "city",
      "catId",
      "cat",
      "category",
      "pType",
      "period",
      "sid",
    ];
    filterKeys.forEach((key) => query.delete(key));
    return query.toString();
  })();
  const storedEmail = getStoredUserEmail();
  const canAccessLiveOrders = isLiveOrderEmail(user?.email ?? storedEmail);
  const [, setTick] = useState(0);
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        setTick((t) => t + 1); // Force a re-render
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);
  // Load sessions initially
  useEffect(() => {
    if (sessionsStatus === "idle") {
      loadSessions();
    }
  }, [sessionsStatus, loadSessions]);

  // Sync selected chat index when sessions or URL sid changes
  useEffect(() => {
    if (!sessions.length) {
      setSelectedChatIndex(null);
      return;
    }
    if (!urlSessionId) {
      setSelectedChatIndex(null);
      return;
    }

    const idx = sessions.findIndex((s) => s.sessionId === urlSessionId);
    setSelectedChatIndex(idx === -1 ? null : idx);
  }, [sessions, urlSessionId]);

  const isActive = (slug: string) => {
    const segs = pathname.split("/").filter(Boolean);
    const first = segs[0];
    if (!slug) {
      // Dashboard is active when not on any named dashboard route
      const baseRoutes = new Set([
        "order-trends",
        "rto-trends",
        "buyer-stats",
        "category-trends",
        "geo-trends",
        "uc-events",
        "live-order",
        "ask-ai",
      ]);
      return !first || !baseRoutes.has(first);
    }
    return first === slug;
  };
  function handleSelectSession(sid: string, idx: number) {
    setSelectedChatIndex(idx);
    isNewChat.current = false;
    handleDrawerToggle(false);
    router.replace(`/ask-ai?sid=${sid}`, { scroll: false });
  }
  const [showLogin, setShowLogin] = useState(false);
  function handleLoginClose() {
    setShowLogin(false);
  }

  const dashboardItems = canAccessLiveOrders
    ? DASHBOARDS
    : DASHBOARDS.filter(
        (d) => d.slug !== "live-order" && d.slug !== "uc-events"
      );

  function handleNewChat() {
    // reset conversational state
    isNewChat.current = true;
    clearMessages();
    setCurrentSessionId(null);
    setSelectedChatIndex(null);
    handleDrawerToggle(false);
    setIsLoading(false);
    setIsStreaming(false);
    setStreamingMsg(null);
    gaService.conversationStarted();
    const authtoken = localStorage.getItem("trends_user") || "";
    if (!authtoken) {
      localStorage.clear();
      // enqueueSnackbar("Your session has expired. Please log in again.", {
      //   variant: "error",
      // });
      setShowLogin(true);
      return;
    }

    // remove sid from URL
    router.replace("/ask-ai", { scroll: false });

    // notify parent if needed
    onNewChat?.();
  }

  // Compute current filter path segments (state/city/category/time) from pathname
  const segments = pathname.split("/").filter(Boolean);
  const baseRoutes = new Set([
    "order-trends",
    "rto-trends",
    "buyer-stats",
    "category-trends",
    "geo-trends",
    "uc-events",
    "live-order",
    "ask-ai",
  ]);
  const pathFilterSegments = (() => {
    if (!segments.length) return [] as string[];
    const [first, ...rest] = segments;
    const raw = baseRoutes.has(first) ? rest : segments;
    if (raw.length >= 4) {
      const firstFour = raw.slice(0, 4);
      const hasLegacyPrefixes = firstFour.some(
        (seg) =>
          seg.startsWith("state-") ||
          seg.startsWith("city-") ||
          seg.startsWith("category-") ||
          seg.startsWith("time-")
      );
      if (!hasLegacyPrefixes) return firstFour;
    }
    return [];
  })();

  const storeFilterSegments = (() => {
    if (!selectedState?.label) return [] as string[];
    const parts: string[] = [];
    parts.push(slugifyLabel(selectedState.label));

    if (!selectedCity || selectedCity.id === ALL_CITY.id) {
      parts.push("all");
    } else if (selectedCity.label) {
      parts.push(slugifyLabel(selectedCity.label));
    }

    if (!selectedCategory || selectedCategory.id === ALL_CATEGORY.id) {
      parts.push("all");
    } else if (selectedCategory.label) {
      parts.push(slugifyLabel(selectedCategory.label));
    } else if (selectedCategory.id) {
      parts.push(String(selectedCategory.id).toLowerCase());
    }

    const timeSlug = toTimeSlug(selectedDateRange);
    if (timeSlug) parts.push(timeSlug);

    return parts;
  })();

  const filterSegments =
    storeFilterSegments.length > 0 ? storeFilterSegments : pathFilterSegments;

  const buildDashboardHref = (slug: string) => {
    const includeFilters = !NON_FILTER_DASHBOARDS.has(slug);
    let segments = includeFilters ? filterSegments : [];

    if (slug === "geo-trends") {
      const rest = segments.length >= 2 ? segments.slice(2) : [];
      segments = ["all", "all", ...rest];
    }

    if (!slug) {
      return segments.length ? `/${segments.join("/")}` : "/";
    }
    return segments.length ? `/${slug}/${segments.join("/")}` : `/${slug}`;
  };

  return (
    <Box
      sx={{
        background: "#141414",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        pb: "max(12px, env(safe-area-inset-bottom))",
      }}
    >
      <LoginModal isOpen={showLogin} onClose={() => handleLoginClose()} />
      {/* Brand */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}
        >
          <img
            src="https://trends.shiprocket.in/assets/icons/logo-white.svg"
            alt="Shiprocket Trends"
            style={{ height: isMobile ? 44 : 80, objectFit: "contain" }}
          />
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              px: "8px",
              height: 22,
              borderRadius: "999px",
              background: "linear-gradient(180deg, #FFE9A6 0%, #F5C34D 100%)",
              color: "#2E1E00",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              border: "1px solid #F3C04A",
              boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              mt:-6
            }}
          >
            Beta
          </Box>
        </Box>
      </Box>

      {/* New chat */}
      <Box sx={{ p: 2, mt: -3 }}>
        <Button
          onClick={handleNewChat}
          size="medium"
          variant="contained"
          startIcon={<AddRoundedIcon />}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            width: "100%",
            bgcolor: "#6540F7",
            "&:hover": { bgcolor: "#5b4ac2" },
          }}
        >
          New chat
        </Button>
      </Box>

      <Box
        sx={{
          minHeight: 0,
          overflowY: "auto",
          p: 2,
        }}
      >
        {/* Curated Dashboards */}
        <List
          dense
          subheader={
            <ListSubheader
              disableSticky
              sx={{
                bgcolor: "transparent",
                color: "#B5B5B5",
                fontSize: 12,
                lineHeight: 1.2,
                letterSpacing: 0.2,
                mb: 0.5,
                mt: 1,
                textTransform: "uppercase",
              }}
            >
              Curated Dashboards
            </ListSubheader>
          }
        >
          {dashboardItems.map((d) => {
            const active = isActive(d.slug);
            const shouldPreserveQuery = !NON_FILTER_DASHBOARDS.has(d.slug);
            const baseHref = buildDashboardHref(d.slug);
            const dashboardHref = `${baseHref}${
              shouldPreserveQuery && dashboardQueryString
                ? `?${dashboardQueryString}`
                : ""
            }`;
            return (
              <ListItem key={d.slug} sx={{ p: "2px 5px" }}>
                <ListItemButton
                  component={Link}
                  href={dashboardHref}
                  selected={active}
                  onClick={() => handleDrawerToggle(false)}
                  sx={{
                    borderRadius: "8px",
                    fontFamily: "sans-serif",
                    gap: 1,
                    ...(active
                      ? {
                          background: "linear-gradient(0deg, #252525, #252525)",
                          border: "1px solid #6540F733",
                        }
                      : {}),
                    "&:hover, &.Mui-selected:hover": {
                      background: "linear-gradient(0deg, #212121, #212121)",
                    },
                  }}
                >
                  <img
                    src={d.icon}
                    alt={d.title}
                    style={{ width: 20, height: 20, opacity: active ? 1 : 0.9 }}
                  />
                  <ListItemText
                    primary={d.title}
                    primaryTypographyProps={{
                      sx: {
                        color: active ? "#FFFFFF" : "#EAEAEA",
                        fontSize: 14,
                        fontWeight: active ? 600 : 400,
                        fontFamily: "'DM Sans', 'Helvetica', sans-serif",
                      },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>

        <Divider
          sx={{ borderColor: "#2A2A2A", my: 2, borderStyle: "dotted" }}
        />

        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 400,
            color: "#B5B5B5",
            px: 0.5,
            mb: 0.5,
          }}
        >
          Recent Chats
        </Typography>

        {/* Chats list OR empty state */}
        <Box sx={{ flex: 1, overflowY: "hidden", pr: 1 }}>
          {sessions.length > 0 ? (
            <List dense>
              {sessions.map((chatItem, idx) => (
                <ListItem key={chatItem.sessionId} sx={{ p: "2px 5px" }}>
                  <ListItemButton
                    selected={selectedChatIndex === idx}
                    onClick={() => {
                      gaService.conversationHistoryOpened(chatItem.sessionId);
                      handleSelectSession(chatItem.sessionId, idx);
                      handleDrawerToggle(false);
                    }}
                    sx={{
                      "&:hover, &.Mui-selected, &.Mui-selected:hover": {
                        background: "linear-gradient(0deg, #212121, #212121)",
                      },
                      borderRadius: "8px",
                      gap: 1,
                    }}
                  >
                    <Stack
                      direction={"column"}
                      spacing={0}
                      mt={"-0.2em"}
                      sx={{
                        width: "100%",
                      }}
                    >
                      <ListItemText
                        primary={chatItem.firstQuestion}
                        slotProps={{
                          primary: {
                            sx: {
                              color: "#EAEAEA",
                              fontSize: 14,
                              width: "100%",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            },
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: 11,
                          color: "#888888",
                          mt: "-4px",
                        }}
                      >
                        {formatRelativeTime(chatItem.createdAt)}
                      </Typography>
                    </Stack>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          ) : (
            // Empty State
            <Box
              sx={{
                mt: 3,
                px: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                flexDirection: "column",
                color: "#B5B5B5",
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "#1E1E1E",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                aria-hidden
              >
                <HistoryOutlinedIcon sx={{ fontSize: 36, color: "#8A8A8A" }} />
              </Box>
              <Box>
                <Typography
                  sx={{ color: "#EAEAEA", fontSize: 14, fontWeight: 500 }}
                >
                  No chat history yet
                </Typography>
                <Typography sx={{ color: "#B5B5B5", fontSize: 12 }}>
                  Start a new conversation to begin
                </Typography>
              </Box>
              <Button
                onClick={handleNewChat}
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                sx={{
                  mt: 0.5,
                  textTransform: "none",
                  borderColor: "#2A2A2A",
                  color: "#EAEAEA",
                  "&:hover": {
                    borderColor: "#3a3a3a",
                    background: "#1B1B1B",
                  },
                  borderRadius: 2,
                }}
              >
                New chat
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

const Sidebar = ({ children, onNewChat }: SidebarProps) => {
  // kept for backwards-compat, even though we now use context sessions
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname() || "/";

  const handleDrawerToggle = () => setMobileOpen((v) => !v);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100dvh",
        position: "relative",
        bgcolor: "#0f0f0f",
      }}
    >
      <div
        className="backdrop_blurred_ellipsis right"
        style={{
          zIndex: "auto",
        }}
      />

      {/* Left rail (desktop) */}
      <Box
        sx={{
          width: { xs: 0, md: drawerWidth },
          flexShrink: 0,
          borderRight: { xs: "none", md: "1px solid #222" },
          display: { xs: "none", md: "block" },
        }}
      >
        <SidebarContent
          pathname={pathname}
          onNewChat={() => {
            Promise.all([setMobileOpen(false), onNewChat?.()]);
          }}
          handleDrawerToggle={setMobileOpen}
        />
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true, disableScrollLock: true }}
        transitionDuration={{ enter: 180, exit: 140 }}
        PaperProps={{
          sx: {
            width: drawerWidth,
            borderRight: "1px solid #222",
            background: "#141414",
            display: { xs: "block", md: "none" },
            willChange: "transform",
            contain: "content",
            WebkitOverflowScrolling: "touch",
          },
        }}
      >
        <SidebarContent
          pathname={pathname}
          onNewChat={onNewChat}
          handleDrawerToggle={setMobileOpen}
        />
      </Drawer>

      {/* RIGHT: CONTENT */}
      <Box
        sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}
      >
        {/* Top bar */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: { xs: 2, md: 6 },
            py: { xs: 1, md: 1.5 },
            background: "#00000066",
            backdropFilter: { xs: "none", md: "blur(20px)" },
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <IconButton
            onClick={handleDrawerToggle}
            aria-label="Open navigation"
            size="small"
            sx={{
              color: "#EAEAEA",
              display: { xs: "inline-flex", md: "none" },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Header showLogo={false} sx={{ mt: 0, mb: 0, flex: 1 }} />
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            minWidth: 0,
            maxHeight: "100dvh",
          }}
          onScroll={(e) => {
            const scrollTop = (e.target as HTMLElement).scrollTop;
            const event = new CustomEvent("sidebar-content-scroll", {
              detail: { scrollTop },
            });
            window.dispatchEvent(event);
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Sidebar;
